import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Grid, 
  Typography, 
  Box,
} from '@mui/material';
import LoadingSkeleton from '../common/LoadingSkeleton';
import axios from 'axios';
import '../Course/CoursePreview.css';
import LearningPathHeader from './LearningPathHeader';
import LearningPathPrerequisites from './LearningPathPrerequisites';
import LearningPathDescription from './LearningPathDescription';
import LearningPathModules from './LearningPathModules';
import LearningPathDetails from './LearningPathDetails';
import LearningPathEnrollment from './LearningPathEnrollment';

import { useUser } from '../../context/UserContext';
import { useToast } from '../../context/ToastContext';

const LearningPathLearnerView = () => {
  const { id: lpathId, instanceId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [lpathData, setLpathData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { user } = useUser();

  const refreshLPathData = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    const fetchLPathData = async () => {
      try {
        setLoading(true);
        // Use the learner-specific endpoint
        const url = `/api/learner/lpath/${lpathId}${instanceId ? `/${instanceId}` : ''}?format=json`;
        const response = await axios.get(url);
        
        if (response.data.success) {
          setLpathData(response.data.data);
        } else {
          throw new Error(response.data.message || 'Failed to load learning path data');
        }
      } catch (err) {
        console.error('Error fetching learning path details:', err);
        setError(err.message || 'An error occurred while loading the learning path');
      } finally {
        setLoading(false);
      }
    };

    fetchLPathData();
  }, [lpathId, instanceId, refreshKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <LoadingSkeleton />
      </Box>
    );
  }

  if (error || !lpathData) {
    return (
      <Container>
        <Typography variant="h6" color="error" align="center">
          {error || 'Learning path not found'}
        </Typography>
      </Container>
    );
  }

  // Ensure all required data has default values
  const { 
    lpath = {}, 
    contents = [], 
    instance = {}, 
    instances = [], 
    contentStats = {
      total: 0,
      completed: 0,
      courses: 0,
      learningPaths: 0
    }, 
    prerequisites = {
      prerequisites: [],
      total: 0,
      completed: 0,
      required: 0
    },
    enrolledObj = null,
    buttonState = { text: 'Enroll', action: 'enroll', isdisabled: false },
    firstIncompleteContent = null,
    progress: serverProgress = null,
    showEnrollCount
  } = lpathData || {};

  // Determine if the user is enrolled
  const enrolled = !!enrolledObj;

  // Calculate progress percentage
  const progressPercentage = serverProgress?.percentage ?? serverProgress?.progress ?? 0;
  const progress = enrolled ? {
    completed: serverProgress?.completed ?? contentStats.completed,
    total: serverProgress?.total ?? contentStats.total,
    progress: progressPercentage
  } : null;

  // Determine if prerequisites are met
  const prerequisitesMet = !prerequisites.prerequisites || 
     prerequisites.prerequisites.length === 0 || 
     prerequisites.required <= prerequisites.completed;
  
  // // Get enrollment status message
  // const getEnrollmentStatusMessage = () => {
  //   if (enrolled) return buttonState.text; // Start, Continue, or Revisit
  //   if (!prerequisitesMet) return "Complete Prerequisites First";
  //   return "Enroll Now";
  // };
  
  // // Determine if the enroll button should be disabled
  // const isEnrollButtonDisabled = buttonState.isdisabled || !prerequisitesMet;

  return (
    <Box sx={{ width: '100%', py: 4, mt: -3 }}>
      <LearningPathHeader 
        lpath={lpath} 
        contentStats={contentStats} 
        enrollmentCount={lpath.enrollment_count || 0} 
        contents={contents}
        userRole={user?.current_role_id}
        showEnrollCount={showEnrollCount}
        progress={progress}
        enrolled={enrolled}
        // enrollButtonDisabled={isEnrollButtonDisabled}
        // enrollStatusMessage={getEnrollmentStatusMessage()}
        prerequisitesMet={prerequisitesMet}
      />
      <Container maxWidth="lg">
        <Grid container spacing={3}>        
          {/* Left column - 8/12 width */}
          <Grid item xs={12} md={8}>
            {prerequisites && prerequisites.prerequisites && prerequisites.prerequisites.length > 0 && (
              <LearningPathPrerequisites 
                prerequisites={prerequisites} 
                parentLPathId={lpathId}
                parentLPathName={lpath.title}
              />
            )}           
            
            
            <LearningPathModules 
              contents={contents} 
              lpathId={lpathId} 
              contentStats={contentStats} 
              enrolled={enrolled}
              lpath={lpath}
              refreshOnClose={refreshLPathData}
              firstIncompleteContent={firstIncompleteContent}
              enrolledObj={enrolledObj}
              instance={instance}
              prerequisitesMet = {prerequisitesMet}
            />
          </Grid>
          
          {/* Right column - 4/12 width */}
          <Grid item xs={12} sx={{
            mb: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }} md={4}>
            <LearningPathEnrollment
              lpath={lpath}
              instance={instance}
              instances={instances}
              enrolled={enrolled}
              enrolledObj={enrolledObj}
              progress={progress}
              contentStats={contentStats}
              prerequisites={prerequisites}
              showEnrollCount={showEnrollCount}
              enrollmentCount={lpath.enrollment_count || 0}
              firstIncompleteContent={firstIncompleteContent}
              refreshOnComplete={refreshLPathData}
            />

            <LearningPathDetails 
              lpath={lpath}
              instance={instance}
              instances={instances}
              contentStats={contentStats}
              progress={progress}
              enrolled={enrolled}              
              enrolledObj={enrolledObj}
              prerequisites={prerequisites}
              showEnrollCount={showEnrollCount}
              enrollmentCount={lpath.enrollment_count || 0}
              firstIncompleteContent={firstIncompleteContent}
              refreshOnComplete={refreshLPathData}
              contents={contents}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LearningPathLearnerView;